import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);
const PlayerContext = createContext(null);

// Generate or retrieve device ID
const getDeviceId = () => {
  let deviceId = localStorage.getItem('aivue_device_id');
  if (!deviceId) {
    // Generate a unique device ID
    deviceId = 'device_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    localStorage.setItem('aivue_device_id', deviceId);
  }
  return deviceId;
};

// ==================== AUTH CONTEXT ====================
const TOKEN_KEY = 'aivue_token';

// Returns the JWT `exp` in ms, or null if the token can't be decoded.
const getTokenExpiry = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.exp ? payload.exp * 1000 : null;
  } catch (_e) {
    return null;
  }
};

// Reads the stored token, discarding it if malformed or expired.
const readStoredToken = () => {
  const stored = localStorage.getItem(TOKEN_KEY);
  if (!stored) return null;
  const exp = getTokenExpiry(stored);
  if (!exp || exp <= Date.now()) {
    localStorage.removeItem(TOKEN_KEY);
    return null;
  }
  return stored;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(readStoredToken);
  const [loading, setLoading] = useState(true);
  const deviceId = getDeviceId();
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const axiosInstance = useMemo(() => {
    const instance = axios.create({
      baseURL: API,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      timeout: 600000 // 10 minutes timeout for large uploads
    });
    // Clear the session when the server rejects the token we sent.
    instance.interceptors.response.use(undefined, (err) => {
      const sentAuth = err.config?.headers?.Authorization;
      if (err.response?.status === 401 && sentAuth && sentAuth === `Bearer ${tokenRef.current}`) {
        logout();
      }
      return Promise.reject(err);
    });
    return instance;
  }, [token, logout]);

  // Auto-logout the moment the token expires.
  useEffect(() => {
    if (!token) return undefined;
    const exp = getTokenExpiry(token);
    if (!exp) return undefined;
    const timer = setTimeout(logout, Math.max(0, Math.min(exp - Date.now(), 2147483647)));
    return () => clearTimeout(timer);
  }, [token, logout]);

  const fetchUser = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/auth/me');
      setUser(res.data);
    } catch (err) {
      logout();
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, logout]);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (email, password) => {
    const res = await axiosInstance.post(`/auth/login?device_id=${deviceId}`, { email, password });
    const { access_token, user: userData } = res.data;
    localStorage.setItem(TOKEN_KEY, access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (username, email, password, country = '', role = 'listener') => {
    const res = await axiosInstance.post(`/auth/register?device_id=${deviceId}`, { username, email, password, country, role });
    const { access_token, user: userData } = res.data;
    localStorage.setItem(TOKEN_KEY, access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const refreshUser = async () => {
    try {
      const res = await axiosInstance.get('/auth/me');
      setUser(res.data);
    } catch (err) {
      // Token may be invalid/expired - user will need to re-login
      if (err.response?.status === 401) {
        logout();
      }
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, axiosInstance, refreshUser, deviceId }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

// ==================== PLAYER CONTEXT ====================
export const PlayerProvider = ({ children }) => {
  const [currentVideo, setCurrentVideo] = useState(null);
  const [queue, setQueue] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState('none'); // none, one, all
  const [volume, setVolume] = useState(1);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMiniPlayerVisible, setIsMiniPlayerVisible] = useState(false);

  const playVideo = useCallback((video, videoList = null, index = 0, startFullscreen = true) => {
    setCurrentVideo(video);
    if (videoList) {
      setQueue(videoList);
      setCurrentIndex(index);
    }
    setIsPlaying(true);
    setIsMiniPlayerVisible(true);
    // Auto-fullscreen by default for better viewing experience
    if (startFullscreen) {
      setIsFullscreen(true);
    }
  }, []);

  // Explicit method for starting in fullscreen mode
  const playVideoFullscreen = useCallback((video, videoList = null, index = 0) => {
    playVideo(video, videoList, index, true);
  }, [playVideo]);

  const playNext = useCallback(() => {
    if (queue.length === 0) return;
    
    let nextIndex;
    if (shuffle) {
      nextIndex = Math.floor(Math.random() * queue.length);
    } else {
      nextIndex = (currentIndex + 1) % queue.length;
    }
    
    if (nextIndex === 0 && repeat === 'none' && !shuffle) {
      setIsPlaying(false);
      return;
    }
    
    setCurrentIndex(nextIndex);
    setCurrentVideo(queue[nextIndex]);
  }, [queue, currentIndex, shuffle, repeat]);

  const playPrevious = useCallback(() => {
    if (queue.length === 0) return;
    
    let prevIndex;
    if (shuffle) {
      prevIndex = Math.floor(Math.random() * queue.length);
    } else {
      prevIndex = currentIndex === 0 ? queue.length - 1 : currentIndex - 1;
    }
    
    setCurrentIndex(prevIndex);
    setCurrentVideo(queue[prevIndex]);
  }, [queue, currentIndex, shuffle]);

  const togglePlay = useCallback(() => {
    setIsPlaying(prev => !prev);
  }, []);

  const toggleShuffle = useCallback(() => {
    setShuffle(prev => !prev);
  }, []);

  const toggleRepeat = useCallback(() => {
    setRepeat(prev => {
      if (prev === 'none') return 'all';
      if (prev === 'all') return 'one';
      return 'none';
    });
  }, []);

  const addToQueue = useCallback((video) => {
    setQueue(prev => [...prev, video]);
  }, []);

  const removeFromQueue = useCallback((index) => {
    setQueue(prev => prev.filter((_, i) => i !== index));
  }, []);

  const clearQueue = useCallback(() => {
    setQueue([]);
    setCurrentIndex(0);
  }, []);

  const closePlayer = useCallback(() => {
    setIsMiniPlayerVisible(false);
    setIsPlaying(false);
    setCurrentVideo(null);
  }, []);

  return (
    <PlayerContext.Provider value={{
      currentVideo,
      queue,
      currentIndex,
      isPlaying,
      shuffle,
      repeat,
      volume,
      progress,
      duration,
      isFullscreen,
      isMiniPlayerVisible,
      playVideo,
      playVideoFullscreen,
      playNext,
      playPrevious,
      togglePlay,
      toggleShuffle,
      toggleRepeat,
      addToQueue,
      removeFromQueue,
      clearQueue,
      closePlayer,
      setVolume,
      setProgress,
      setDuration,
      setIsPlaying,
      setIsFullscreen,
      setIsMiniPlayerVisible
    }}>
      {children}
    </PlayerContext.Provider>
  );
};

export const usePlayer = () => useContext(PlayerContext);
